import React from 'react'

import clsx from 'clsx'

import Accordion from 'components/Accordion'
import CtaButton from 'components/CtaButton'
import Divider from 'components/Divider'
import Typography from 'components/Typography'
import { useEventContext } from 'context/EventContext'
import openUrl from 'helpers/openUrl'

import ItemCard from './components/ItemCard'

import classes from './Faq.module.scss'

/** @type {import("./types").default} */
const Faq = ({ listing }) => {
  const { sendEvent } = useEventContext()

  const eventContext = useEventContext({
    engagementComponent: 'faq',
    name: 'engagement_product_click',
  })
  const accordionOnExpandClick = (event, { isExpanded }) => {
    sendEvent({
      engagementComponent: 'faq',
      engagementDestination: isExpanded ? 'expand' : 'collapse',
      engagementDetails: 'expansion-panel',
      name: 'engagement_product_click',
    })
  }

  const hasKeyTakeaways = !!listing.key_takeaways_summary
  const keyTakeaway = listing.key_takeaways_summary
  const keyTakeawaySummary = keyTakeaway?.summary
  const hasKeyTakeawaySummary = hasKeyTakeaways && keyTakeawaySummary.length > 0
  const hasKeyTakeawayAiSummary = hasKeyTakeaways && keyTakeaway?.key_takeaways.length > 0
  const hasOnlyUseCasesSummary =
    hasKeyTakeawaySummary &&
    keyTakeaway.key_takeaways.length === 1 &&
    keyTakeaway.key_takeaways[0].title === 'Users and use cases'
  const structuredData = listing.faqStructuredData ? listing.faqStructuredData : listing.faq

  if (!hasOnlyUseCasesSummary && hasKeyTakeawayAiSummary && listing.benefits) {
    const exists = structuredData.some(item => item.question === `Q. What are the benefits of using ${listing.name}?`)
    if (!exists)
      structuredData.splice(1, 0, {
        question: `Q. What are the benefits of using ${listing.name}?`,
        answer: listing.benefits.replace(/\n/g, '<br/>'),
      })
  }

  if (hasKeyTakeawaySummary && listing.overview) {
    const exists = structuredData.some(item => item.question === `Q. What is ${listing.name} used for?`)
    if (!exists)
      structuredData.splice(1, 0, { question: `Q. What is ${listing.name} used for?`, answer: listing.overview })
  }

  const getFaq = (faq, i, detailComponent, list) => (
    <React.Fragment key={faq.question}>
      <Accordion
        summary={<p className={classes.question}>{`${faq.question}`}</p>}
        summaryClassname={classes.accordionSummary}
        details={detailComponent}
        paperProps={{ square: !(i === 0 || i === list.length - 1) }}
        defaultExpanded={i == 0}
        onExpandClick={accordionOnExpandClick}
      />
      {i + 1 !== list.length && <Divider className={classes.divider} />}
    </React.Fragment>
  )

  const showBetterValueForMoney = () => {
    return listing.alternatives_by_section?.faq_contenders?.length > 0
  }

  const getBetterValueForMoney = () => (
    <>
      <Typography className={classes.contendersTitle} variant={'heading-xs'}>
        These products have better value for money
      </Typography>
      <div className={classes.contendersWrapper}>
        {listing.alternatives_by_section.faq_contenders.slice(0, 3).map((faq_contender, index) => (
          <ItemCard
            key={faq_contender.id}
            listing={faq_contender}
            eventProps={{
              name: 'engagement_product_clickout',
              engagementComponent: 'faq',
              engagementDetails: 'card_product',
              engagementDestination: faq_contender.is_premium ? 'go-to_vendor-website' : 'go-to_product_pricing',
              productClickUUID: faq_contender.global_product_id,
            }}
            listingPosition={index}
          />
        ))}
      </div>
    </>
  )

  const showFreeAlternativesButton = () => {
    return listing.cta_urls?.faq_contenders && listing.cta_urls.faq_contenders.trim() !== ''
  }

  const FirstFAQItemContent = () => (
    <>
      {showBetterValueForMoney() && getBetterValueForMoney()}
      {showFreeAlternativesButton() && (
        <CtaButton
          onClick={() => openUrl(listing.cta_urls.faq_contenders, { target: '_self' })}
          rel={listing.is_premium ? 'nofollow noopener' : undefined}
          self={!listing.is_premium}
          className={classes.ctaButton}
          {...eventContext.addEvent({
            engagementDestination: 'go-to_free_serp',
            engagementDetails: 'button_see-free-alternatives',
          })}
        >
          See free alternatives
        </CtaButton>
      )}
    </>
  )

  const NotFirstFAQItemContent = () => (
    <CtaButton
      onClick={() => openUrl(listing.cta_urls.alternatives, { target: '_self' })}
      rel={listing.is_premium ? 'nofollow noopener' : undefined}
      self={!listing.is_premium}
      className={classes.ctaButton}
      {...eventContext.addEvent({
        engagementDestination: 'go-to_product_alternatives',
        engagementDetails: 'button_see-alternatives',
      })}
    >
      See alternatives
    </CtaButton>
  )

  const parseLinksToHtml = text => {
    // More comprehensive URL regex
    const urlRegex =
      /https?:\/\/(www\.)?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)/g

    return text.replace(urlRegex, url => {
      // Clean up trailing punctuation
      const cleanUrl = url.replace(/[.,;!?]+$/, '')

      return `<a href="${cleanUrl}" target="_blank" rel="noopener noreferrer" style="color: #11817f; text-decoration: underline;">${cleanUrl}</a>`
    })
  }

  return (
    <div className={clsx('Faq', classes.root)}>
      {listing.faq.map((faq, i) =>
        getFaq(
          faq,
          i,
          <>
            <p
              className={classes.answer}
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: parseLinksToHtml(faq.answer) }}
            />

            {i === 0 ? <FirstFAQItemContent /> : <NotFirstFAQItemContent />}
          </>,
          listing.faq
        )
      )}

      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: structuredData.map(({ question, answer }) => ({
              '@type': 'Question',
              name: question,
              acceptedAnswer: {
                '@type': 'Answer',
                text: parseLinksToHtml(answer.replace(/<\/?[^>]+(>|$)/g, ' ')),
              },
            })),
          }),
        }}
      />
    </div>
  )
}

export default Faq
